import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Matrix } from '@babylonjs/core/Maths/math.vector';

export type RigPrimitiveKind = 'box' | 'cylinder';

interface PrimitiveUvData {
  positions: ArrayLike<number> | null;
  normals: ArrayLike<number> | null;
  uvs: ArrayLike<number> | null;
}

function fractional(value: number): number {
  return value - Math.floor(value);
}

/**
 * Stable per-part offset that prevents every beam and vessel from showing the same
 * patch of a scan. The offset changes placement only; it never changes physical scale.
 */
function uvPhase(transform: Matrix, variation: number): readonly [number, number] {
  const centre = Vector3.TransformCoordinates(Vector3.Zero(), transform);
  const u = Math.sin(
    centre.x * 12.9898 + centre.y * 78.233 + centre.z * 37.719 + variation * 19.19,
  ) * 43758.5453;
  const v = Math.sin(
    centre.x * 93.989 + centre.y * 67.345 + centre.z * 11.135 + variation * 47.77,
  ) * 24634.6345;
  return [fractional(u), fractional(v)];
}

function transformedAxisLength(transform: Matrix, x: number, y: number, z: number): number {
  return Vector3.TransformNormal(new Vector3(x, y, z), transform).length();
}

/**
 * Rebuild primitive UVs in metres instead of treating every object as a 0..1 square.
 *
 * Babylon's primitive builders duplicate vertices at every face/cap, so retaining their
 * original UV directions keeps normal-map handedness intact. Only each UV island's span
 * changes: a ten-metre edge now covers ten times as much texture as a one-metre edge.
 */
export function createPhysicalUvs(
  data: PrimitiveUvData,
  transform: Matrix,
  kind: RigPrimitiveKind,
  tilesPerMetre: number,
  variation = 1,
): number[] {
  if (!Number.isFinite(tilesPerMetre) || tilesPerMetre <= 0) {
    throw new RangeError('tilesPerMetre must be finite and positive');
  }
  if (!Number.isFinite(variation)) {
    throw new RangeError('variation must be finite');
  }
  if (!data.positions || !data.normals || !data.uvs) {
    throw new RangeError('rig primitive must contain positions, normals, and UVs');
  }

  const determinant = transform.determinant();
  if (!Number.isFinite(determinant) || determinant <= 0) {
    throw new RangeError('rig primitive transform must have a positive determinant');
  }

  const xMetres = transformedAxisLength(transform, 1, 0, 0);
  const yMetres = transformedAxisLength(transform, 0, 1, 0);
  const zMetres = transformedAxisLength(transform, 0, 0, 1);
  const [phaseU, phaseV] = uvPhase(transform, variation);
  const result: number[] = [];

  for (let vertex = 0; vertex < data.positions.length / 3; vertex++) {
    const sourceU = Number(data.uvs[vertex * 2]);
    const sourceV = Number(data.uvs[vertex * 2 + 1]);
    const nx = Math.abs(Number(data.normals[vertex * 3]));
    const ny = Math.abs(Number(data.normals[vertex * 3 + 1]));
    const nz = Math.abs(Number(data.normals[vertex * 3 + 2]));

    let uMetres: number;
    let vMetres: number;

    if (kind === 'box') {
      if (nx > ny && nx > nz) {
        // Babylon's ±X islands run U along local Y and V along local Z.
        uMetres = yMetres;
        vMetres = zMetres;
      } else if (ny > nz) {
        // The ±Y islands run U along local Z and V along local X.
        uMetres = zMetres;
        vMetres = xMetres;
      } else {
        uMetres = xMetres;
        vMetres = yMetres;
      }
    } else if (ny > 0.5) {
      // Cylinder caps retain Babylon's planar disc UV layout.
      uMetres = xMetres;
      vMetres = zMetres;
    } else {
      // All current cylinders are circular, but Ramanujan's ellipse approximation also
      // keeps future non-uniform X/Z scaling physically credible.
      const a = xMetres * 0.5;
      const b = zMetres * 0.5;
      const h = ((a - b) * (a - b)) / ((a + b) * (a + b));
      uMetres = Math.PI * (a + b) * (1 + (3 * h) / (10 + Math.sqrt(4 - 3 * h)));
      vMetres = yMetres;
    }

    result.push(
      sourceU * uMetres * tilesPerMetre + phaseU,
      sourceV * vMetres * tilesPerMetre + phaseV,
    );
  }

  return result;
}
